import {
  ArrowBackRounded,
  AssignmentRounded,
  DirectionsRounded,
  DownloadRounded,
  EditOutlined,
  ErrorOutlineRounded,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Rating,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import AttachmentIcon from "../../../assets/attachment.svg";
import PDFIcon from "../../../assets/ph_file-pdf.svg";
import XLSIcon from "../../../assets/ph_file-xls.svg";
import Loading from "../../../components/loading/loading";
import { ToolbarButton } from "../../../components/toolbar_button/toolbar_button";
import MainLayout from "../../../layouts/main/main_layout";
import {
  downloadNeed,
  getArea,
  getBrand,
  getNeed,
  getNeedsFilters,
  getNeedsStatus,
  patchNeedStatus,
} from "../../../services/needs/needs_service";
import { black, greyEnabled, purple, purpleLight } from "../../../theme/colors";
import { downloadFromBlob } from "../../../utils/utils";
import StatusMenu from "../components/status_menu/status_menu";
import { Need } from "../model/need";
import { useEffect, useState } from "react";
import { queryClient } from "../../../providers/query/query";
import UpdateNeedStatus from "../components/update_need_status/update_need_status";
import { NeedFilters, Option } from "../model/needs_filters";
import useUser from "../../../providers/user/user_provider";
import { useFeedback } from "../../../providers/feedback/feeedback";

const HEADER_ICON_BADGE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 44,
  height: 44,
  borderRadius: 2,
  bgcolor: purpleLight,
  flexShrink: 0,
} as const;

const DROPDOWN_POPOVER_SX = {
  mt: 0.5,
  borderRadius: 2,
  border: "1px solid #ECECEC",
  boxShadow: "0px 12px 28px -8px rgba(27,0,27,0.18)",
} as const;

const INPUT_BORDER_SX = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#ABA0AB" },
    "&:hover fieldset": { borderColor: purple },
    "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
  },
} as const;

function extractErrorMessage(error: any, fallback: string): string {
  if (error?.response?.status === 403) {
    return "You do not have permission to perform this operation.";
  }
  return error?.response?.data?.error?.message || fallback;
}

export default function Details() {
  const navigate = useNavigate();
  const [needStatus, setNeedStatus] = useState<string | null>(null);
  const [isNeedStatusChanged, setNeedStatusChanged] = useState<boolean>(false);
  const [closeNeedStatusDialog, setCloseNeedStatusDialog] =
    useState<boolean>(false);
  const [resolveNeedStatusDialog, setResolveNeedStatusDialog] =
    useState<boolean>(false);
  const [isResolvingNeed, setIsResolvingNeed] = useState(false);
  const [modifyEnabled, setModifyEnabled] = useState(false);
  const [_categoryId, _setCategoryId] = useState<number | null>(null);
  const [_subCategoryId, _setSubcategoryId] = useState<number | null>(null);
  const [_brand, _setBrandId] = useState<number | null>(null);
  const [_areaId, _setAreaId] = useState<number | null>(null);
  const [internalNotes, setInternalNotes] = useState("");
  const [brands, setBrands] = useState([]);
  const [areas, setAreas] = useState([]);
  const [editInternalNote, setEditInternalNote] = useState(false);
  const [isSavingStatus, setIsSavingStatus] = useState(false);
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [isClosingNeed, setIsClosingNeed] = useState(false);
  const { showSnackbar } = useFeedback()

  const getAreaApiCall = () => {
    getArea({ categoryId: _categoryId, subcategoryId: _subCategoryId }).then(
      (res) => {
        setAreas(res.data.data.list);
      }
    ).catch((error) => {
      showSnackbar(extractErrorMessage(error, "Something went wrong while loading areas."));
    });
  };

  const getGrandsApiCall = () => {
    getBrand({ categoryId: _categoryId, subcategoryId: _subCategoryId }).then(
      (res) => {
        setBrands(res.data.data.list);
      }
    ).catch((error) => {
      showSnackbar(extractErrorMessage(error, "Something went wrong while loading brands."));
    });
  };

  let { id } = useParams();
  const { hasPermission } = useUser();
  const { data: needDetails, isLoading, isError } = useQuery<Need>(
    ["need-details", id],
    async () => {
      let data = await getNeed(id!);

      return data?.data?.data?.data;
    },
    {
      onError: (error: any) => {
        showSnackbar(extractErrorMessage(error, "Something went wrong while loading need details."));
      },
    }
  );

  // The statuses an admin may set (In Progress, Resolved, Closed) - their
  // ids, since the StatusMenu/updateNeedStatus flow needs the real
  // status_id to PATCH with rather than a hardcoded guess.
  const { data: statusOptions } = useQuery(["needs-status"], async () => {
    let response = await getNeedsStatus();
    return (response?.data?.data?.list || []) as { id: number; name: string }[];
  });
  const statusIdByName = (name: string) =>
    statusOptions?.find((status) => status.name === name)?.id;

  // Brand/area lookups are only needed for the SysGen modify dropdowns, so
  // they're gated behind modifyEnabled instead of firing on every load.
  useEffect(() => {
    if (modifyEnabled && _subCategoryId) {
      getGrandsApiCall();
      getAreaApiCall();
    }
  }, [_subCategoryId, modifyEnabled]);

  useEffect(() => {
    _setCategoryId(needDetails?.systemCategoryId || null);
    _setSubcategoryId(needDetails?.systemSubCategoryId || null);
    _setAreaId(needDetails?.systemAreaId || null);
    _setBrandId(needDetails?.systemBrandId || null);
    setInternalNotes(needDetails?.systemNotes || "");
  }, [needDetails]);

  // Only needed to populate the SysGen modify dropdowns — fetching it
  // eagerly on every details page load was an unnecessary call for anyone
  // who never opens the modify form.
  const { data: needsCategory } = useQuery<NeedFilters>(
    ["needs-filters"],
    async () => {
      let data = await getNeedsFilters();

      return data?.data?.data;
    },
    { enabled: modifyEnabled }
  );

  function getSubCategories(): Option[] {
    return _categoryId
      ? needsCategory?.categories?.find(
        (category) => category.category._id === _categoryId
      )?.subcategory || []
      : [];
  }

  function listItem({
    title,
    value,
  }: {
    title: string;
    value: any;
  }): JSX.Element {
    return (
      <Box>
        <Typography variant="body2" color="primary.main" fontWeight={600}>
          {title}
        </Typography>
        <Typography variant="body1">{value || "-"}</Typography>
      </Box>
    );
  }

  function invalidateNeedsQueries() {
    queryClient.invalidateQueries({ queryKey: ["need-details", id] });
    queryClient.invalidateQueries({ queryKey: ["needs"] });
  }

  function downloadPdf() {
    downloadNeed(id!, "pdf").then((response) => {
      downloadFromBlob(response, new Date().valueOf() + ".pdf");
    }).catch((error) => {
      showSnackbar(extractErrorMessage(error, "Something went wrong while downloading the PDF."));
    });
  }

  function downloadExcel() {
    downloadNeed(id!, "excel").then((response) => {
      downloadFromBlob(response, new Date().valueOf() + ".xlsx");
    }).catch((error) => {
      showSnackbar(extractErrorMessage(error, "Something went wrong while downloading the file."));
    });
  }

  function updateNeedStatus() {
    if (needStatus === "In Progress") {
      const statusId = statusIdByName("In Progress");
      if (!statusId) {
        showSnackbar("Statuses are still loading, please try again in a moment.");
        return;
      }
      const formData = new FormData();
      formData.append("status", String(statusId));
      if (internalNotes) {
        formData.append("notes", internalNotes);
      }
      setIsSavingStatus(true);
      patchNeedStatus(id!, formData).then(() => {
        invalidateNeedsQueries();
        setNeedStatusChanged(false);
        setEditInternalNote(false);
        setInternalNotes("");
        showSnackbar("Need status updated successfully.");
      }).catch((error) => {
        showSnackbar(extractErrorMessage(error, "Something went wrong while updating the status."));
      }).finally(() => setIsSavingStatus(false));
    } else if (needStatus === "Resolved") {
      setResolveNeedStatusDialog(true);
    } else if (needStatus === "Closed") {
      setCloseNeedStatusDialog(true);
    } else if (needStatus === "Submitted") {
      const formData = new FormData();
      formData.append("status", "1");
      if (internalNotes) {
        formData.append("notes", internalNotes);
      }
      setIsSavingStatus(true);
      patchNeedStatus(id!, formData).then(() => {
        invalidateNeedsQueries();
        setNeedStatusChanged(false);
        setEditInternalNote(false);
        setInternalNotes("");
        showSnackbar("Need status updated successfully.");
      }).catch((error) => {
        showSnackbar(extractErrorMessage(error, "Something went wrong while updating the status."));
      }).finally(() => setIsSavingStatus(false));
    }
  }

  function updateNeedStatusToClosed(comments: string | null, file: File | null, notificationMode: string[]) {
    const statusId = statusIdByName("Closed");
    if (!statusId) {
      showSnackbar("Statuses are still loading, please try again in a moment.");
      return;
    }
    const formData = new FormData();
    formData.append("status", String(statusId));
    if (comments) {
      formData.append("comments", comments);
      // The admin now must send closureRemarks specifically to close a
      // need - the existing "Closing Feedback" field already captures
      // exactly that, so the same text is sent as both.
      formData.append("closureRemarks", comments);
    }
    if (internalNotes) {
      formData.append("notes", internalNotes);
    }
    if (file) {
      formData.append("file", file);
    }
    if (notificationMode.length)
      formData.append("type", JSON.stringify(notificationMode));
    setIsClosingNeed(true);
    patchNeedStatus(id!, formData).then(() => {
      invalidateNeedsQueries();
      setNeedStatusChanged(false);
      setEditInternalNote(false);
      setInternalNotes("");
      setCloseNeedStatusDialog(false);
      showSnackbar("Need marked as closed.");
    }).catch((error) => {
      showSnackbar(extractErrorMessage(error, "Something went wrong while closing the need."));
    }).finally(() => setIsClosingNeed(false));
  }

  function updateNeedStatusToResolved(comments: string | null, file: File | null, notificationMode: string[]) {
    const statusId = statusIdByName("Resolved");
    if (!statusId) {
      showSnackbar("Statuses are still loading, please try again in a moment.");
      return;
    }
    const formData = new FormData();
    formData.append("status", String(statusId));
    if (comments) {
      formData.append("comments", comments);
      // Resolving a need now requires resolutionRemarks specifically - the
      // "Resolution Remarks" field already captures exactly that.
      formData.append("resolutionRemarks", comments);
    }
    if (internalNotes) {
      formData.append("notes", internalNotes);
    }
    if (file) {
      formData.append("file", file);
    }
    if (notificationMode.length)
      formData.append("type", JSON.stringify(notificationMode));
    setIsResolvingNeed(true);
    patchNeedStatus(id!, formData).then(() => {
      invalidateNeedsQueries();
      setNeedStatusChanged(false);
      setEditInternalNote(false);
      setInternalNotes("");
      setResolveNeedStatusDialog(false);
      showSnackbar("Need marked as resolved.");
    }).catch((error) => {
      showSnackbar(extractErrorMessage(error, "Something went wrong while resolving the need."));
    }).finally(() => setIsResolvingNeed(false));
  }

  function updateNeedDetails() {
    const formData = new FormData();
    let status = "1"
    if (status === "In Progress") {
      status = "2"
    } else if (status === "Closed") {
      status = "3"
    }

    formData.append("status", status); // The
    if (_categoryId) {
      formData.append("sysgenCategory", _categoryId.toString());
    }
    if (_subCategoryId) {
      formData.append("sysgenSubCategory", _subCategoryId.toString());
    }
    if (_areaId) {
      formData.append("sysgenArea", _areaId.toString());
    }
    if (_brand) {
      formData.append("sysgenBrand", _brand.toString());
    }
    setIsSavingDetails(true);
    patchNeedStatus(
      id!,
      formData
    ).then(() => {
      invalidateNeedsQueries();
      _setCategoryId(null);
      _setAreaId(null);
      _setSubcategoryId(null);
      showSnackbar("Need details updated successfully.");
    }).catch((error) => {
      showSnackbar(extractErrorMessage(error, "Something went wrong while updating the need details."));
    }).finally(() => setIsSavingDetails(false));
  }

  useEffect(() => {
    if (needDetails?.status) setNeedStatus(needDetails.status);
  }, [needDetails]);

  function _dropdownOption({
    title,
    placeholder,
    options,
    selectedId,
    onSelect,
    disabled,
  }: {
    title: string;
    placeholder: string;
    options: { id: number; value: string }[];
    selectedId: number | null;
    onSelect: (id: number | null) => void;
    disabled?: boolean;
  }) {
    return (
      <Box mr={4}>
        <Box pt={1} pb={2} display="flex" flexDirection="row">
          <Autocomplete
            disabled={disabled}
            fullWidth
            // multiple
            value={
              selectedId == null
                ? { id: null, value: "" }
                : options.find((option) => selectedId === option?.id)
            }
            onChange={(e, value) => {
              onSelect(value?.id || null);
            }}
            options={options}
            getOptionLabel={(option) => option?.value}
            componentsProps={{
              paper: { elevation: 0, sx: DROPDOWN_POPOVER_SX },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={placeholder}
                sx={INPUT_BORDER_SX}
              />
            )}
          />
        </Box>
      </Box>
    );
  }

  function checkExtension(extension: string) {
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'];
    const documentExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv'];
    const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm', 'quicktime'];
    const audioExtensions = ['mp3', 'wav', 'aac', 'ogg', 'flac', 'm4a', 'wma'];

    extension = extension?.toLowerCase();

    if (imageExtensions.includes(extension)) return "Image";
    if (documentExtensions.includes(extension)) return "Document";
    if (videoExtensions.includes(extension)) return "Video";
    if (audioExtensions.includes(extension)) return "Audio";

    return "Unknown";
  }

  return (
    <MainLayout>
      <Box display="flex" alignItems="center" gap={1.5} mb={2}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackRounded />
        </IconButton>
        <Box sx={HEADER_ICON_BADGE}>
          <AssignmentRounded sx={{ color: purple, fontSize: 24 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700} color={black}>
            Need Details
          </Typography>
          <Typography variant="body2" color={greyEnabled}>
            View and manage a reported need.
          </Typography>
        </Box>
      </Box>
      <Grid container spacing={3}>
        <Grid item md={8} lg={7} sm={12} xs={12}>
          <Card
            elevation={0}
            sx={{ width: "100%", borderRadius: 3, border: "1px solid #ECECEC" }}
          >
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              {isLoading ? (
                <Box
                  height={300}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Loading />
                </Box>
              ) : isError ? (
                <Box
                  height={300}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  gap={1}
                >
                  <ErrorOutlineRounded sx={{ color: greyEnabled, fontSize: 32 }} />
                  <Typography color={greyEnabled}>
                    Something went wrong while loading need details.
                  </Typography>
                </Box>
              ) : !needDetails ? (
                <Box
                  height={300}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  gap={1}
                >
                  <ErrorOutlineRounded sx={{ color: greyEnabled, fontSize: 32 }} />
                  <Typography color={greyEnabled}>Need not found.</Typography>
                </Box>
              ) : (
                <Box overflow="auto" flex={1}>
                  <Grid container spacing={3}>
                    <Grid item md={6}>
                      {listItem({
                        title: "Reference Number",
                        value: needDetails?.needId,
                      })}
                    </Grid>
                    <Grid item md={6}>
                      {listItem({
                        title: "Full Name",
                        value: needDetails?.name,
                      })}
                    </Grid>
                    <Grid item md={6}>
                      {listItem({
                        title: "Contact Number",
                        value: `+91 ${needDetails?.mobileNumber}`,
                      })}
                    </Grid>
                    <Grid item md={6}>
                      {listItem({
                        title: "Category",
                        value: needDetails?.category,
                      })}
                    </Grid>
                    <Grid item md={6}>
                      {listItem({
                        title:
                          needDetails?.category === "Corporate"
                            ? "Company"
                            : "Sub Category",
                        value: needDetails?.subCategory,
                      })}
                    </Grid>
                    <Grid item md={6}>
                      {listItem({
                        title: "Area",
                        value: needDetails?.area,
                      })}
                    </Grid>
                    {needDetails?.category === "Corporate" && (
                      <Grid item md={6}>
                        {listItem({
                          title: "Brand",
                          value: needDetails?.brand,
                        })}
                      </Grid>
                    )}
                  </Grid>
                  <Grid
                    container
                    rowGap={3}
                    my={2}
                    p={2}
                    sx={{ border: `1px solid ${purpleLight}`, borderRadius: 3 }}
                  >
                    {!modifyEnabled ? (
                      <>
                        <Grid item md={6}>
                          {listItem({
                            title: "SysGen Category",
                            value: needDetails?.systemCategory
                              ? needDetails?.systemCategory
                              : "NA",
                          })}
                        </Grid>
                        <Grid item md={6}>
                          {listItem({
                            title:
                              needDetails?.systemCategory === "Corporate"
                                ? "SysGen Company"
                                : "SysGen Sub Category",
                            value: needDetails?.systemSubCategory
                              ? needDetails?.systemSubCategory
                              : "NA",
                          })}
                        </Grid>
                        <Grid item md={6}>
                          {listItem({
                            title: "SysGen Area",
                            value: needDetails?.systemArea
                              ? needDetails.systemArea
                              : "NA",
                          })}
                        </Grid>
                        <Grid item md={6}>
                          {needDetails?.systemCategory === "Corporate" &&
                            listItem({
                              title: "SysGen Brand",
                              value: needDetails?.systemBrand,
                            })}
                        </Grid>
                        {hasPermission("needs", "update") && <Grid item md={4}>
                          <Button
                            disabled={
                              needDetails?.status === 'Closed'}
                            variant="outlined"
                            fullWidth
                            onClick={() => setModifyEnabled(true)}
                            sx={{
                              borderColor: purple,
                              color: purple,
                              borderRadius: 2,
                              textTransform: "none",
                              fontWeight: 600,
                              "&:hover": { borderColor: purple, bgcolor: purpleLight },
                            }}
                          >
                            Modify
                          </Button>
                        </Grid>}
                      </>
                    ) : (
                      <>
                        <Grid item md={6}>
                          {_dropdownOption({
                            title: "SysGen Category",
                            placeholder: "Search for SysGen Category",
                            options:
                              needsCategory?.categories?.map((cat) => {
                                return {
                                  id: cat.category._id,
                                  value: cat.category.name,
                                };
                              }) || [],
                            selectedId: _categoryId,
                            onSelect: (id) => {
                              _setCategoryId(id);
                              _setSubcategoryId(null);
                              _setAreaId(null);
                              _setBrandId(null);
                            },
                          })}
                        </Grid>
                        <Grid item md={6}>
                          {_dropdownOption({
                            disabled: _categoryId === 3 ? true : false,
                            title:
                              _categoryId === 2
                                ? "SysGen Company"
                                : "SysGen Sub-Category",
                            placeholder:
                              _categoryId === 2
                                ? "SysGen Company"
                                : "SysGen Sub-Category",
                            options: getSubCategories()?.map((option) => {
                              return { id: option._id, value: option.name };
                            }),
                            selectedId: _subCategoryId,
                            onSelect: (id) => {
                              _setSubcategoryId(id);
                              _setBrandId(null);
                              _setAreaId(null);
                            },
                          })}
                        </Grid>
                        {_categoryId === 2 && (
                          <Grid item md={6}>
                            {_dropdownOption({
                              disabled: brands?.length === 0 ? true : false,
                              title: "SysGen Brand",
                              placeholder: "Search for SysGen Brand",
                              options: brands || [],
                              selectedId: _brand,
                              onSelect: (id) => {
                                _setBrandId(id);
                              },
                            })}
                          </Grid>
                        )}
                        <Grid item md={6}>
                          {_dropdownOption({
                            disabled: areas?.length === 0 ? true : false,
                            title: "SysGen Area",
                            placeholder: "Search for SysGen Area",
                            options: areas || [],
                            selectedId: _areaId,
                            onSelect: (id) => {
                              _setAreaId(id);
                            },
                          })}
                        </Grid>
                        {_categoryId !== 2 && <Grid item md={6}></Grid>}
                        <Grid item md={4}>
                          <Button
                            variant="contained"
                            disableElevation
                            fullWidth
                            disabled={isSavingDetails}
                            startIcon={isSavingDetails ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : undefined}
                            onClick={() => {
                              updateNeedDetails();
                              setModifyEnabled(false);
                            }}
                            sx={{
                              bgcolor: purple,
                              borderRadius: 2,
                              textTransform: "none",
                              fontWeight: 600,
                              "&:hover": { bgcolor: "#7A2074" },
                            }}
                          >
                            Save Changes
                          </Button>
                        </Grid>
                        <Grid item md={4}>
                          <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => {
                              setModifyEnabled(false);
                            }}
                            sx={{
                              ml: 2,
                              borderColor: purple,
                              color: purple,
                              borderRadius: 2,
                              textTransform: "none",
                              fontWeight: 600,
                              "&:hover": { borderColor: purple, bgcolor: purpleLight },
                            }}
                          >
                            Cancel
                          </Button>
                        </Grid>
                      </>
                    )}
                  </Grid>
                  <Grid container spacing={3}>
                    <Grid item md={6}>
                      {listItem({
                        title: "Area",
                        value: needDetails?.area,
                      })}
                    </Grid>
                    <Grid item md={6}>
                      {listItem({
                        title: "Location",
                        value: needDetails?.address,
                      })}
                    </Grid>
                    <Grid item md={6}>
                      {listItem({
                        title: "State",
                        value: needDetails?.state,
                      })}
                    </Grid>
                    <Grid item md={6}>
                      {listItem({
                        title: "City",
                        value: needDetails?.city,
                      })}
                    </Grid>
                    <Grid item md={6}>
                      {listItem({
                        title: "District",
                        value: needDetails?.district,
                      })}
                    </Grid>
                    <Grid item md={12}>
                      {listItem({
                        title: "Description",
                        value: needDetails?.description,
                      })}
                    </Grid>
                    <Grid item md={12}>
                      {listItem({
                        title: "Action Expected",
                        value: needDetails?.action,
                      })}
                    </Grid>
                    {needDetails?.lon && needDetails?.lon && (
                      <Grid item md={12}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="body2" color="primary.main" fontWeight={600}>
                            Location details
                          </Typography>
                          <a
                            target="_blank"
                            href={`https://www.google.com/maps/dir/?api=1&destination=${needDetails?.lat},${needDetails?.lon}`}
                            rel="noreferrer"
                          >
                            <Button
                              size="small"
                              variant="text"
                              endIcon={<DirectionsRounded />}
                              sx={{ color: purple, textTransform: "none", fontWeight: 600 }}
                            >
                              Direction
                            </Button>
                          </a>
                        </Box>
                        <Box
                          height={160}
                          mt={1}
                          sx={{ borderRadius: 3, overflow: "hidden", border: "1px solid #ECECEC" }}
                        >
                          <iframe
                            title="map"
                            width="100%"
                            height="100%"
                            frameBorder={0}
                            src={`https://maps.google.com/maps?q=${needDetails?.lat},${needDetails?.lon}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                          />
                        </Box>
                      </Grid>
                    )}

                    {needDetails?.documents?.length !== 0 && (
                      <Grid item md={12}>
                        {needDetails?.documents?.[0].url && <Typography variant="body2" color="primary.main" fontWeight={600}>
                          Supporting Documents
                        </Typography>}
                        <List dense={true}>
                          {needDetails?.documents?.map((document: { fileExtension: string, url: string }, index) => {
                            let docType = checkExtension(document.fileExtension)
                            return (
                              <>
                                {document?.url ? <Box key={index}>
                                  <Typography variant="body2" mt={1} fontWeight={600}>
                                    {docType}
                                  </Typography>
                                  <ListItem
                                    sx={{
                                      border: `1px solid ${purpleLight}`,
                                      borderRadius: 3,
                                      mb: 2,
                                      pr: 0,
                                      mt: 1
                                    }}
                                  >

                                    <img src={AttachmentIcon} alt="" />
                                    <ListItemText
                                      primary={"Document"}
                                      primaryTypographyProps={{ ml: 1, }}
                                    ></ListItemText>
                                    <ListItemIcon>
                                      <a
                                        href={document.url}
                                        target="_blank"
                                        download={document.url}
                                        rel="noreferrer"
                                      >
                                        <IconButton sx={{ color: purple }}>
                                          <DownloadRounded />
                                        </IconButton>
                                      </a>
                                    </ListItemIcon>
                                  </ListItem>
                                </Box> : <Box key={index}></Box>}
                              </>

                            );
                          })}
                        </List>
                      </Grid>
                    )}
                    {needDetails?.feedback || needDetails?.rating ? (
                      <>
                        <Grid item md={6}>
                          {listItem({
                            title: "User feedback",
                            value: needDetails?.feedback,
                          })}
                        </Grid>
                        <Grid item md={6}>
                          {listItem({
                            title: "User rating",
                            value: (
                              <Rating value={needDetails?.rating} readOnly />
                            ),
                          })}
                        </Grid>
                      </>
                    ) : null}
                    <Grid item md={12}>
                      <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
                        <Box>
                          <StatusMenu
                            status={needDetails?.status || ""}
                            updatedStatus={needStatus}
                            onStatusChange={(status) => {
                              setNeedStatus(status);
                              setNeedStatusChanged(true);
                            }}
                          />
                        </Box>

                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1.5}
                        >
                          <ToolbarButton icon={XLSIcon} label="Export Excel" onClick={downloadExcel} />
                          <ToolbarButton icon={PDFIcon} label="Export PDF" onClick={downloadPdf} />
                        </Box>
                      </Box>
                    </Grid>

                    {(editInternalNote ||
                      needDetails?.systemNotes === "" ||
                      needDetails?.systemNotes === null) ? (
                      <Grid item md={12}>
                        {hasPermission("needs", "update") && <TextField
                          fullWidth
                          value={internalNotes}
                          rows={4}
                          multiline={true}
                          placeholder="Enter internal notes if any"
                          label="Internal note"
                          InputLabelProps={{
                            shrink: true,
                          }}
                          onChange={(e) => {
                            setInternalNotes(e.target.value);
                          }}
                          sx={INPUT_BORDER_SX}
                          InputProps={{
                            endAdornment: (
                              <Button
                                variant="text"
                                onClick={() => {
                                  setEditInternalNote(false);
                                  setInternalNotes(
                                    needDetails?.systemNotes || ""
                                  );
                                }}
                                sx={{
                                  position: "absolute",
                                  bottom: 0,
                                  right: 0,
                                  color: purple,
                                  textTransform: "none",
                                }}
                              >
                                Clear
                              </Button>
                            ),
                          }}
                        />}
                      </Grid>
                    ) : (
                      (needDetails?.status !== 'Closed' || needDetails?.systemNotes) &&
                      <Grid item md={12}>
                        <Box>
                          <Box display="flex" flexDirection={"row"}>
                            <Typography variant="body2" color="primary.main" fontWeight={600}>
                              {"Internal Note"}
                            </Typography>

                            {hasPermission("needs", "update") && <EditOutlined
                              sx={{
                                height: 16,
                                width: 16,
                                ml: 1,
                                cursor: "pointer",
                                color: greyEnabled,
                              }}
                              onClick={() =>
                                setEditInternalNote(true)
                              }
                            />}
                          </Box>
                          <Typography>{needDetails?.systemNotes}</Typography>
                        </Box>
                      </Grid>
                    )}
                    {needDetails?.systemComments !== "" && (
                      <Grid item md={12}>
                        <Box>
                          <Typography variant="body2" color="primary.main" fontWeight={600}>
                            {"Admin Feedback"}
                          </Typography>
                          <Typography>
                            {needDetails?.systemComments}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    <Grid item md={12}>
                      <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
                        <Button
                          variant="outlined"
                          sx={{
                            pl: 2,
                            pr: 2,
                            textTransform: "none",
                            fontWeight: 600,
                            minWidth: 200,
                            borderColor: purple,
                            color: purple,
                            borderRadius: 2,
                            "&:hover": { borderColor: purple, bgcolor: purpleLight },
                          }}
                          onClick={() => navigate(-1)}
                        >
                          Back To Needs
                        </Button>
                        {hasPermission("needs", "update") && <Button
                          variant="contained"
                          disableElevation
                          sx={{
                            pl: 4,
                            pr: 4,
                            textTransform: "none",
                            fontWeight: 600,
                            minWidth: 200,
                            bgcolor: purple,
                            borderRadius: 2,
                            "&:hover": { bgcolor: "#7A2074" },
                          }}
                          disabled={
                            needDetails?.status === 'Closed' ? true : internalNotes !== ""
                              ? false
                              : !isNeedStatusChanged || isSavingStatus
                          }
                          startIcon={isSavingStatus ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : undefined}
                          onClick={() => {
                            updateNeedStatus()
                          }}
                        >
                          Save
                        </Button>}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <UpdateNeedStatus
        open={closeNeedStatusDialog}
        mode="close"
        submitting={isClosingNeed}
        onDialogClose={() => {
          setCloseNeedStatusDialog(false);
        }}
        onSubmit={updateNeedStatusToClosed}
      />
      <UpdateNeedStatus
        open={resolveNeedStatusDialog}
        mode="resolve"
        submitting={isResolvingNeed}
        onDialogClose={() => {
          setResolveNeedStatusDialog(false);
        }}
        onSubmit={updateNeedStatusToResolved}
      />
    </MainLayout>
  );
}
